/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  input,
  output,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  effect,
  Injector,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, filter, takeUntil } from 'rxjs';
import { Router, NavigationEnd } from '@angular/router';
import { SidebarComponent, NebulaNavigationService } from 'nebula-ui-kit';
import {
  AuthService,
  VaultMenuAccessService,
  VaultSidebarMenuService,
  resetVaultSidebarPermissionsPending,
  syncRoleIdsIntoUserData,
  type VaultSidebarMenuItem,
} from '@nebula/shared-vault/data-access';
import { SidebarStateService } from '@nebula-layout/layout';
import { SidebarMenuItem } from '@nebula-layout/layout';
import { BreadcrumbSyncService } from '@nebula-layout/layout';
import { applyVaultMenuLabels } from '../../../../utils/vault-menu-labels';
import { ensurePreservationMessagesSidebarItem } from '../../../../utils/preservation-messages-menu.util';
import { filterVaultSidebarMenuItems } from '../../../../utils/vault-sidebar-hidden-menu.util';
import { RouteSyncService } from '@nebula-layout/layout';
import { buildBreadcrumbItemsFromRouter } from '../../../../utils/breadcrumb-from-router.util';
import { isVaultRoute } from 'libs/layout/src/lib/utils/nebula-route-validity';
import { VAULT_MAX_OPEN_TABS } from '../../../../../const/src/lib/layout.constants';
import {
  countVaultOpenTabs,
  findOpenVaultTab,
  normalizeVaultMenuRoute,
} from '../../../../utils/vault-open-tabs.util';

@Component({
  selector: 'app-sidebar-wrapper-2',
  standalone: true,
  imports: [CommonModule, SidebarComponent],
  template: `
    <nav class="sidebar-nav" [attr.aria-label]="sidebarTitle()">
      <lib-sidebar
        [menuItems]="menuItems"
        [collapsed]="collapsed()"
        [isLoading]="isLoading"
        [logoUrl]="logoUrl()"
        [logoAlt]="logoAlt()"
        (itemClick)="onItemClick($event)"
        (collapsedChange)="onToggleSidebar()"
        [title]="sidebarTitle()"
      />
    </nav>

    @if (vaultTabsLimitToastMsg) {
      <div class="vault-tabs-limit-toast" role="status" aria-live="polite">
        <span class="vault-tabs-limit-toast__message">{{ vaultTabsLimitToastMsg }}</span>
      </div>
    }
  `,
  styleUrls: ['./sidebar-wrapper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarWrapperComponent2 implements OnInit, OnDestroy {
  private static readonly VAULT_NON_INGEST_SECTIONS = new Set([
    'dashboard',
    'preservation',
    'retention',
    'legal-hold',
    'reports',
    'cloud',
    'notifications',
    'audit',
    'settings',
    'admin',
    'preservation-messages',
    'repository-admin',
  ]);
  readonly collapsed = input<boolean>(false);
  readonly collapsedChange = output<boolean>();
  readonly routeSelected = output<string>();
  readonly logoUrl = input<string | undefined>();
  readonly logoAlt = input<string>('Nebula App');

  readonly vaultSidebarMenu = inject(VaultSidebarMenuService);
  readonly authService = inject(AuthService);
  readonly menuAccess = inject(VaultMenuAccessService);
  readonly router = inject(Router);
  readonly navService = inject(NebulaNavigationService);
  readonly sidebarState = inject(SidebarStateService);
  readonly cdr = inject(ChangeDetectorRef);
  readonly injector = inject(Injector);
  readonly breadcrumbSync = inject(BreadcrumbSyncService);

  readonly destroy$ = new Subject<void>();
  vaultTabsLimitToastMsg: string | null = null;
  private vaultTabsLimitToastTimeout: ReturnType<typeof setTimeout> | null = null;
  private currentProductId: string | null = null;
  private reloadTimeout: ReturnType<typeof setTimeout> | null = null;
  /** Evita aplicar respuestas obsoletas cuando hay varias recargas seguidas (login / refresh). */
  private menuLoadSeq = 0;
  private menuRetryTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly onVaultSessionUpdated = () => {
    syncRoleIdsIntoUserData();
    resetVaultSidebarPermissionsPending();
    this.reloadMenuWithDelay(true);
  };
  readonly routeSync = inject(RouteSyncService);

  isLoading = false;
  menuItems: SidebarMenuItem[] = [];

  readonly sidebarTitle = computed(() => {
    return 'Nebula Vault';
  });

  ngOnInit(): void {
    this.loadMenu();
    this.listenToProductChanges();
    this.listenToRouteChanges();
    this.listenToSessionChanges();
    this.listenToAuthChanges();
    queueMicrotask(() => this.syncBreadcrumbFromCurrentRoute());
  }

  /** Tras nuevo login (p. ej. cierre automático de sesión), recargar menú con roles ya persistidos. */
  private listenToAuthChanges(): void {
    this.authService.isAuthenticated$
      .pipe(
        filter((authenticated) => authenticated),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        syncRoleIdsIntoUserData();
        resetVaultSidebarPermissionsPending();
        this.reloadMenuWithDelay();
      });
  }

  /** Recarga menú cuando cambia sesión/rol en localStorage (nuevo login o JWT de empresa). */
  private listenToSessionChanges(): void {
    if (typeof window === 'undefined') return;

    window.addEventListener('storage', (event) => {
      if (event.key === 'user_data' || event.key === 'access_token') {
        this.reloadMenuWithDelay();
      }
    });

    window.addEventListener('vault-session-updated', this.onVaultSessionUpdated);
  }

  private listenToProductChanges(): void {
    effect(
      () => {
        const productId = localStorage.getItem('selectedProductId');
        if (productId !== this.currentProductId) {
          this.currentProductId = productId;
          this.reloadMenuWithDelay();
        }
      },
      { injector: this.injector },
    );
  }

  private listenToRouteChanges(): void {
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.syncBreadcrumbFromCurrentRoute();
        const newProductId = localStorage.getItem('selectedProductId');
        if (newProductId !== this.currentProductId) {
          this.currentProductId = newProductId;
          this.reloadMenuWithDelay();
        }
        if (event.urlAfterRedirects.includes('/layout/vault')) {
          this.ensureVaultMenuLoaded();
        }
      }
    });
  }

  private ensureVaultMenuLoaded(): void {
    if (!this.authService.isAuthenticated()) return;

    syncRoleIdsIntoUserData();
    if (!this.menuItems.length) {
      this.reloadMenuWithDelay();
    }

    if (this.menuRetryTimeout) {
      clearTimeout(this.menuRetryTimeout);
    }
    this.menuRetryTimeout = setTimeout(() => {
      if (!this.menuItems.length && this.authService.isAuthenticated()) {
        syncRoleIdsIntoUserData();
        this.reloadMenu();
      }
    }, 400);
  }

  private syncBreadcrumbFromCurrentRoute(): void {
    const id = this.navService.activeTabId();
    const tab = this.navService.tabs().find((t) => t.id === id);
    const items = buildBreadcrumbItemsFromRouter(this.router, {
      getIconForMenuLabel: (label) => this.getSidebarParentIcon(label),
      lastMenuOrTabIcon: (tab?.icon as string) || 'File',
    });
    if (items.length > 0) {
      this.breadcrumbSync.update(items);
    }
  }

  private reloadMenuWithDelay(skipRoleSync = false): void {
    if (this.reloadTimeout) {
      clearTimeout(this.reloadTimeout);
    }

    this.reloadTimeout = setTimeout(() => {
      this.reloadMenu(skipRoleSync);
    }, 150);
  }

  reloadMenu(skipRoleSync = false): void {
    if (!this.menuItems.length) {
      this.isLoading = true;
    }
    this.menuLoadSeq += 1;
    this.cdr.markForCheck();
    this.loadMenu(this.menuLoadSeq, skipRoleSync);
  }

  loadMenu(requestSeq?: number, skipRoleSync = false): void {
    const seq = requestSeq ?? ++this.menuLoadSeq;
    if (!this.menuItems.length) {
      this.isLoading = true;
      this.cdr.markForCheck();
    }

    this.vaultSidebarMenu
      .getVaultSidebarMenu({ skipRoleSync })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: VaultSidebarMenuItem[]) => {
          this.applySidebarMenu(data, seq);
        },
        error: () => {
          this.applySidebarMenu(this.vaultSidebarMenu.getCanonicalFallbackMenu(), seq);
        },
      });
  }

  private applySidebarMenu(data: VaultSidebarMenuItem[], requestSeq: number): void {
    if (requestSeq !== this.menuLoadSeq) {
      return;
    }

    let items = filterVaultSidebarMenuItems(
      ensurePreservationMessagesSidebarItem(
        applyVaultMenuLabels(this.processMenuRoutesStatic(data)),
      ),
    );

    if (!items.length) {
      items = filterVaultSidebarMenuItems(
        ensurePreservationMessagesSidebarItem(
          applyVaultMenuLabels(
            this.processMenuRoutesStatic(this.vaultSidebarMenu.getCanonicalFallbackMenu()),
          ),
        ),
      );
    }

    this.menuItems = items;
    this.isLoading = false;
    this.cdr.markForCheck();
  }

  private processMenuRoutesStatic(menu: SidebarMenuItem[]): SidebarMenuItem[] {
    const processItem = (item: SidebarMenuItem): SidebarMenuItem => {
      const processed = { ...item };

      if (processed.route) {
        processed.route = this.normalizeRouteStatic(processed.route);
      }

      if (processed.children?.length) {
        processed.children = processed.children.map((child) => processItem(child));
      }

      return processed;
    };

    return menu.map((item) => processItem(item));
  }

  private normalizeRouteStatic(route: string): string {
    let normalized = route;

    normalized = normalized.replace(
      /\/layout\/(accounting|tesorería|tesoreria|budget)\/\1\//g,
      '/layout/$1/',
    );
    normalized = normalized.replace(/\/+/g, '/');
    normalized = normalized.replace(/\/admin\/users(\/|$)/gi, '/admin/user$1');
    if (!normalized.startsWith('/')) normalized = '/' + normalized;

    const targetModule = this.resolveVaultLayoutModuleKey();

    normalized = normalized.replace(
      /^\/layout\/(accounting|tesorería|tesoreria|budget|vault)\//,
      `/layout/${targetModule}/`,
    );

    if (!normalized.match(/^\/layout\/(accounting|tesorería|tesoreria|budget|vault)\//)) {
      const cleanPath = normalized
        .replace(/^\/(accounting|tesorería|tesoreria|budget|vault)\//, '')
        .replace(/^\//, '');
      normalized = `/layout/${targetModule}/${cleanPath}`;
    }

    return normalized;
  }

  /** Evita reescribir rutas Vault a accounting cuando selectedRoute no es exactamente "vault". */
  private resolveVaultLayoutModuleKey(): string {
    if (typeof window !== 'undefined') {
      const path = window.location.pathname.toLowerCase();
      if (path.includes('/layout/vault')) return 'vault';
    }

    const selected = (localStorage.getItem('selectedRoute') ?? '').toLowerCase();
    if (
      selected === 'vault' ||
      selected.includes('vault') ||
      selected.includes('bóveda') ||
      selected.includes('boveda') ||
      selected.includes('nebula')
    ) {
      return 'vault';
    }

    const validModules = ['accounting', 'tesorería', 'tesoreria', 'budget', 'vault'];
    if (validModules.includes(selected)) {
      return selected === 'tesoreria' ? 'tesorería' : selected;
    }

    return 'vault';
  }

  private detectModule(route: string): string {
    const routeLower = route.toLowerCase();

    const budgetKeywords = [
      'budget',
      'presupuesto',
      'budget-parameters',
      'resource-type',
      'funding-source',
    ];

    const tesoreriaKeywords = [
      'tesorería',
      'tesoreria',
      'treasury',
      'bank',
      'bank-account',
      'pago',
      'payment',
      'banco',
      'resource-type',
      'embargos',
    ];

    const vaultKeywords = [
      'vault',
      'dashboard',
      'ingest',
      'preservation',
      'retention',
      'legal-hold',
      'reports',
      'cloud',
      'notifications',
      'audit',
      'settings',
      'admin',
    ];

    if (budgetKeywords.some((keyword) => routeLower.includes(keyword))) {
      return 'budget';
    }

    if (tesoreriaKeywords.some((keyword) => routeLower.includes(keyword))) {
      return 'tesorería';
    }

    if (vaultKeywords.some((keyword) => routeLower.includes(keyword))) {
      return 'vault';
    }

    return 'accounting';
  }

  private getRouteKey(route: string | undefined | null): string {
    if (!route) return '';

    let normalized = route.toLowerCase();
    normalized = normalized.split('?')[0].split('#')[0];
    normalized = normalized.replace(/\/+/g, '/');

    normalized = normalized.replace(/tesoreria/g, 'tesorería');
    normalized = normalized.replace(/\/layout\/(tesoreria)\//g, '/layout/tesorería/');

    return normalized;
  }

  private getSidebarParentIcon(parentLabel: string): string {
    const normalized = parentLabel.trim().toLowerCase();
    const parent = this.menuItems.find((m) => m.label.trim().toLowerCase() === normalized);
    return parent?.icon || 'Folder';
  }

  onItemClick(item: any, domEvent?: Event): void {
    domEvent?.preventDefault();
    domEvent?.stopPropagation();

    if (Array.isArray(item.children) && item.children.length > 0) {
      return;
    }

    const routeToUse = item.route;
    if (!routeToUse || typeof routeToUse !== 'string' || routeToUse.trim() === '') {
      return;
    }

    const finalRoute = normalizeVaultMenuRoute(routeToUse);
    const label = String(item.label || item.name || item.title || 'Sin título');
    const icon = item.icon || undefined;

    this.suppressKitTabOpen(item);

    const existingTab = findOpenVaultTab(this.navService.tabs(), finalRoute);
    if (existingTab?.id != null) {
      this.navService.selectTab(existingTab.id);
      void this.router.navigateByUrl(finalRoute).then((success) => {
        if (success) this.routeSelected.emit(finalRoute);
      });
      this.cdr.markForCheck();
      return;
    }

    // Tope 8 para TODOS los roles / con o sin permiso: no crea pestaña nueva.
    if (countVaultOpenTabs(this.navService.tabs()) >= VAULT_MAX_OPEN_TABS) {
      this.notifyVaultMaxOpenTabs();
      return;
    }

    if (!isVaultRoute(finalRoute)) {
      this.menuAccess.notifyVaultToast(`Ruta no disponible: ${finalRoute}`, 'Ruta no disponible');
      return;
    }

    if (!this.menuAccess.isRouteAllowed(finalRoute)) {
      this.menuAccess.notifyAccessDenied(
        this.menuAccess.getLabelForRoute(finalRoute) || label,
        finalRoute,
      );
      return;
    }

    this.navService.addTabFromMenu({
      label,
      route: finalRoute,
      icon,
    });

    void this.router.navigateByUrl(finalRoute).then((success) => {
      if (success) this.routeSelected.emit(finalRoute);
    });

    this.cdr.markForCheck();
  }

  private suppressKitTabOpen(item: { route?: string }): void {
    const routeBackup = item.route;
    item.route = undefined;
    queueMicrotask(() => {
      item.route = routeBackup;
    });
  }

  private notifyVaultMaxOpenTabs(): void {
    if (this.vaultTabsLimitToastTimeout) {
      clearTimeout(this.vaultTabsLimitToastTimeout);
      this.vaultTabsLimitToastTimeout = null;
    }

    this.vaultTabsLimitToastMsg = `Máximo ${VAULT_MAX_OPEN_TABS} pestañas abiertas. Cierra alguna para abrir otra.`;
    this.cdr.markForCheck();

    this.vaultTabsLimitToastTimeout = setTimeout(() => {
      this.vaultTabsLimitToastMsg = null;
      this.vaultTabsLimitToastTimeout = null;
      this.cdr.markForCheck();
    }, 2500);
  }

  onToggleSidebar(): void {
    const newCollapsedState = !this.collapsed();
    this.collapsedChange.emit(newCollapsedState);
    this.sidebarState.setState(!newCollapsedState, true);
  }

  ngOnDestroy(): void {
    if (this.reloadTimeout) {
      clearTimeout(this.reloadTimeout);
    }
    if (this.menuRetryTimeout) {
      clearTimeout(this.menuRetryTimeout);
    }
    if (this.vaultTabsLimitToastTimeout) {
      clearTimeout(this.vaultTabsLimitToastTimeout);
    }

    if (typeof window !== 'undefined') {
      window.removeEventListener('vault-session-updated', this.onVaultSessionUpdated);
    }

    this.destroy$.next();
    this.destroy$.complete();
  }
}
